import React from "react"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useLocation } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import {
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  type MeterGridRecord,
  meterToGridRecord,
  useAdminMetersGridColumns,
} from "@/modules/admin/meters"
import {
  SiteReadonlyFields,
  useAdminSitePageState,
} from "@/modules/admin/sites"
import { DataGridToolbar } from "@/nzds/data-grid"
import { useMeters } from "@/services/meter"
import { useUrl } from "@/utils/url"

import { Box, Button, Link as MuiLink, Stack, Typography } from "@mui/material"
import type { GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

export const AdminSiteRoute: React.FC = () => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminMeterPath, adminSiteEditPath, adminSitesPath } = useUrl()
  const { siteData, siteId, siteIsLoading, siteIsUnavailable } =
    useAdminSitePageState()

  const organizationId = activeOrganizationId ?? ""

  const {
    metersData,
    metersIsFetching,
    metersIsPending,
    metersIsPlaceholderData,
    metersIsRefetching,
  } = useMeters({
    apiQueryOptions: {
      extraFields: ["emission_factor_display"],
      includes: ["meter_source_type", "site"],
      where: {
        organization_id: organizationId,
        site_id: siteId,
      },
    },
    queryOptions: {
      enabled:
        Boolean(organizationId) &&
        siteId !== undefined &&
        siteData != null &&
        !siteIsUnavailable,
      throwOnError: false,
    },
  })

  const meters = React.useMemo(() => {
    if (metersIsPlaceholderData) {
      return []
    }
    return metersData ?? []
  }, [metersData, metersIsPlaceholderData])

  const metersGridColumns = useAdminMetersGridColumns({
    fields: ["code", "provider", "sourceTypeName", "emissionFactorDisplay"],
  })

  const actionsColumn = useAdminGridActionsColumn<MeterGridRecord>({
    getActions: ({ id }) => [
      createAdminGridViewAction(adminMeterPath(String(id)), t("button.view")),
    ],
  })
  const meterColumns = React.useMemo(
    () => [...metersGridColumns, actionsColumn],
    [actionsColumn, metersGridColumns]
  )
  const meterRows = React.useMemo(() => meters.map(meterToGridRecord), [meters])
  const isMetersGridLoading =
    metersIsPending || metersIsFetching || metersIsRefetching

  if (!organizationId) {
    return null
  }

  if (siteIsUnavailable) {
    return <Page404 />
  }

  let siteFieldsContent: React.ReactNode = null
  if (siteIsLoading) {
    siteFieldsContent = (
      <AdminEntityFieldsSkeleton
        ariaLabel={t("loadingAriaLabel", {
          title: t("admin.siteDetail.title"),
        })}
      />
    )
  } else if (siteData != null) {
    siteFieldsContent = <SiteReadonlyFields site={siteData} />
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <PageTitle
        breadcrumbActions={
          <MuiLink component={Link} to={`${adminSitesPath}${search}`}>
            {t("admin.sites")}
          </MuiLink>
        }
        heading={t("admin.siteDetail.title")}
        primaryActions={
          <Button
            component={NavLink}
            disabled={siteIsLoading}
            to={`${adminSiteEditPath(String(siteId))}${search}`}
            variant="contained"
          >
            {t("button.edit")}
          </Button>
        }
      />
      <PageCard>
        <Stack sx={{ gap: 2 }}>
          {siteFieldsContent}
          <Box sx={{ pt: 1 }}>
            <Typography component="h2" variant="subtitle1">
              {t("editSite.metersLabel")}
            </Typography>
          </Box>
          <Box sx={{ height: 320 }}>
            <NzDataGrid
              aria-label={t("editSite.metersGridAriaLabel")}
              columns={meterColumns}
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              getRowId={(row: MeterGridRecord) => row.id}
              loading={siteIsLoading || isMetersGridLoading}
              initialState={{ pinnedColumns: { right: ["actions"] } }}
              rows={meterRows}
              slotProps={{
                toolbar: {
                  isRecordCountLoading:
                    metersIsFetching || metersIsPlaceholderData,
                  recordCount: metersData?.length,
                } as GridSlotProps["toolbar"],
              }}
              slots={{
                toolbar: DataGridToolbar as GridSlots["toolbar"],
              }}
            />
          </Box>
        </Stack>
      </PageCard>
    </Stack>
  )
}
